import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Container, Typography, Grid, Pagination,
  Stack, Alert, InputBase, Paper,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useCatalog } from '../../context/CatalogContext';
import ContentCard from '../../components/common/ContentCard';
import FilterBar from '../../components/common/FilterBar';
import LoadingSkeleton from '../../components/common/LoadingSkeleton';
import usePagination from '../../hooks/usePagination';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import useIsMobile from '../../hooks/useIsMobile';

const PAGE_SIZE = 12;

function applyFilters(items, filters, search, sort) {
  let result = [...items];
  if (search) {
    const q = search.toLowerCase();
    result = result.filter(
      (m) =>
        m.title?.toLowerCase().includes(q) ||
        m.summary?.toLowerCase().includes(q) ||
        (m.products || []).some((p) => p.toLowerCase().includes(q))
    );
  }
  if (filters.levels?.length)
    result = result.filter((m) => (m.levels || []).some((l) => filters.levels.includes(l)));
  if (filters.products?.length)
    result = result.filter((m) => (m.products || []).some((p) => filters.products.includes(p)));
  if (filters.roles?.length)
    result = result.filter((m) => (m.roles || []).some((r) => filters.roles.includes(r)));
  switch (sort) {
    case 'popularity':    result.sort((a, b) => (b.popularity || 0) - (a.popularity || 0)); break;
    case 'rating':        result.sort((a, b) => (b.rating?.average || 0) - (a.rating?.average || 0)); break;
    case 'newest':        result.sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified)); break;
    case 'duration_asc':  result.sort((a, b) => (a.duration_in_minutes || 0) - (b.duration_in_minutes || 0)); break;
    case 'duration_desc': result.sort((a, b) => (b.duration_in_minutes || 0) - (a.duration_in_minutes || 0)); break;
    case 'title':         result.sort((a, b) => (a.title || '').localeCompare(b.title || '')); break;
    default: break;
  }
  return result;
}

export default function ModulesPage() {
  const { modules, taxonomy, loading, error, loadModules } = useCatalog();
  const isMobile = useIsMobile();
  const [search, setSearch]   = useState('');
  const [filters, setFilters] = useState({ levels: [], products: [], roles: [] });
  const [sort, setSort]       = useState('popularity');

  useEffect(() => { if (modules.length === 0) loadModules(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => applyFilters(modules, filters, search, sort), [modules, filters, search, sort]);
  const pagination = usePagination(filtered, PAGE_SIZE);
  const infinite   = useInfiniteScroll(filtered, PAGE_SIZE);

  const handleFilterChange = useCallback((f) => { setFilters(f); pagination.reset(); }, [pagination]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} letterSpacing="-1px" gutterBottom>
          Browse Modules
        </Typography>
        <Typography color="text.secondary">
          Explore {modules.length.toLocaleString()} Microsoft Learn modules
        </Typography>
      </Box>

      <Paper sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, mb: 3, borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: 'none' }}>
        <SearchIcon sx={{ color: 'text.disabled', mr: 1.5 }} />
        <InputBase fullWidth placeholder="Search modules..." value={search}
          onChange={(e) => { setSearch(e.target.value); pagination.reset(); }} />
      </Paper>

      <FilterBar taxonomy={taxonomy} filters={filters} onChange={handleFilterChange}
        sortValue={sort} onSortChange={(v) => { setSort(v); pagination.reset(); }}
        totalCount={modules.length} filteredCount={filtered.length} />

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading && modules.length === 0 ? (
        <LoadingSkeleton count={PAGE_SIZE} />
      ) : (
        <>
          {!isMobile && (
            <>
              <Grid container spacing={2.5}>
                {pagination.pageItems.map((m) => (
                  <Grid key={m.uid} item xs={12} sm={6} md={4} lg={3}>
                    <ContentCard item={m} variant="module" />
                  </Grid>
                ))}
              </Grid>
              {pagination.totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 5 }}>
                  <Pagination count={pagination.totalPages} page={pagination.page}
                    onChange={(_, p) => pagination.goTo(p)} color="primary" shape="rounded" size="large" />
                </Stack>
              )}
            </>
          )}
          {isMobile && (
            <>
              <Grid container spacing={2}>
                {infinite.visibleItems.map((m) => (
                  <Grid key={m.uid} item xs={12} sm={6}>
                    <ContentCard item={m} variant="module" />
                  </Grid>
                ))}
              </Grid>
              <div ref={infinite.sentinelRef} style={{ height: 40, marginTop: 16 }} />
              {!infinite.hasMore && filtered.length > 0 && (
                <Typography align="center" color="text.disabled" sx={{ mt: 2, pb: 4 }}>
                  All {filtered.length} modules loaded
                </Typography>
              )}
            </>
          )}
          {filtered.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 12 }}>
              <Typography variant="h6" color="text.secondary">No modules found</Typography>
              <Typography color="text.disabled" sx={{ mt: 1 }}>Try adjusting your search or filters</Typography>
            </Box>
          )}
        </>
      )}
    </Container>
  );
}
